/**
 * Tiered elevation dimensions — the DEFAULT set of dimensions an elevation
 * carries, and the one that prints.
 *
 * The older generator (`calculateElevationMeasurements`) gives every fixture
 * TYPE its own chain from wall edge to wall edge, so a kitchen wall with
 * cabinets, a range, a dishwasher, a sink and a hood stacks a row per type per
 * side — 19 rows on one real wall, with the drawing an eighth of the image.
 * That generator survives as the on-screen "All dimensions" mode.
 *
 * Here each side carries at most a few STRINGS, the way a drawing is
 * dimensioned by hand:
 *
 *   bottom   detail  → every break of every low object
 *            major   → openings and the ends of each run of low objects
 *            overall → the wall
 *   top      detail  → every break of every high object (only when it adds one)
 *   one side heights → floor, every vertical break, ceiling
 *            overall → floor to ceiling
 *
 * Output is the SAME measurement shape the renderer already draws, including
 * the `object-start|center|end` / `wall-*` / `floor` / `ceiling` point types
 * that `@shared-lib/dimensioning` keys a measurement by — so hides, drag
 * offsets and the details panel work unchanged.
 *
 * Design: claude-docs/2026-09-17-elevation-tiered-dimensions.md §3.
 */

import { HIGH_OBJECT_FLOOR_M } from './floorPlanUtils';
import {
  TYPE_PRIORITY,
  getObjectHeight,
  getObjectTypeLabel,
  getObjectWidth,
  isCustomFixture,
  isHorizontalOnlyFixture,
  isMeasurableObject,
} from './elevationMeasurementUtils';

/** Innermost dimension line, in px from the drawing. Matches the stagger layout. */
export const TIER_BASE_OFFSET_PX = 40;
/** Distance between two dimension lines on the same side, in px. */
export const TIER_PITCH_PX = 24;

/**
 * How far the dimension apparatus stands off the drawing.
 *
 * On SCREEN the drawing is fitted to the window, so 40 / 24 px reads well. In
 * a CAPTURE those same px are paper: 40 px is 0.42" and three lines take
 * nearly an inch — around a room-package elevation only 2.3" wide, printed
 * into a cell barely wider. Reserving that much shrank the drawing to
 * 1/16" = 1'. Print therefore uses a tighter set, which is also closer to how
 * a small-scale drawing is dimensioned by hand.
 */
export const TIER_GEOMETRY = {
  screen: { baseOffsetPx: 40, pitchPx: 24, labelAllowancePx: 16 },
  // The pitch has to clear TWO label rows, because a tight label flips to the
  // far side of its line (elevationStringLabels). At 12 px it landed in the
  // next line's lane and the three rows read as one smear.
  capture: { baseOffsetPx: 20, pitchPx: 26, labelAllowancePx: 12 },
};
/** Two breaks closer than this are ONE break (1 mm, as the older generator). */
const NODE_MERGE_M = 0.001;
/** A dimension shorter than this is not worth drawing (1 cm, as before). */
const MIN_SEGMENT_M = 0.01;

/** The strings a side can carry, innermost first. */
export const TIERS = {
  bottom: ['detail', 'major', 'overall'],
  top: ['detail'],
  vertical: ['heights', 'overall'],
};

/**
 * How far the dimensions reach beyond the drawing, in px, for `n` lines. The
 * tiers make this KNOWABLE: a capture can size its padding and a sheet can
 * size its cell, instead of guessing a fixed 260 px and clipping whatever ran
 * past it.
 */
export const tierDepthPx = (tierCount, geometry = TIER_GEOMETRY.capture) =>
  tierCount <= 0
    ? 0
    : geometry.baseOffsetPx +
      (tierCount - 1) * geometry.pitchPx +
      geometry.labelAllowancePx;

/** The most lines any one side can carry. */
export const MAX_TIERS = {
  bottom: TIERS.bottom.length,
  top: TIERS.top.length,
  side: TIERS.vertical.length,
};

/** Padding a tiered capture needs on every side. */
export const TIERED_CAPTURE_PADDING_PX = tierDepthPx(
  Math.max(MAX_TIERS.bottom, MAX_TIERS.top, MAX_TIERS.side),
  TIER_GEOMETRY.capture
);

/**
 * A fridge is a kitchen elevation's most load-bearing break — the opening its
 * cabinetry is built around — but `refrigerator` is classed as a furnishing
 * (never measured) and `fridge` falls through as an unknown type (centre
 * only). In the tiers both give EDGES. The older generator keeps its own
 * classification.
 */
const FRIDGE_TYPES = new Set(['refrigerator', 'fridge']);

const normalizedType = (obj) => getObjectTypeLabel(obj).toLowerCase();

const isFridge = (obj) => FRIDGE_TYPES.has(normalizedType(obj));

/** Doors, windows and cased openings — a major break whatever height it is. */
const isOpeningBreak = (obj) => {
  const category = obj?.category || {};
  if (category.door || category.window || category.opening) return true;
  const type = normalizedType(obj);
  return type === 'door' || type === 'window';
};

/** Does this object get a break at each EDGE, or one at its CENTRE? */
const hasEdgeBreaks = (obj) => isCustomFixture(obj) || isFridge(obj);

/** Everything the tiers may measure on this segment. */
const measurableForTiers = (obj) => isMeasurableObject(obj) || isFridge(obj);

/**
 * High = the object's BOTTOM is at or above the plan's 48" cut plane, the same
 * rule that dashes an above-cut object in plan (`floorPlanUtils`). Reading a
 * "top half of the wall" instead put upper cabinets on the BOTTOM string as
 * soon as a ceiling passed ~12 ft.
 */
const isHighObject = (obj, scale, floorY) => {
  const bottomY = obj.y2D + getObjectHeight(obj, scale) / 2;
  return (floorY - bottomY) / scale >= HIGH_OBJECT_FLOOR_M;
};

/**
 * Break positions (px along the axis) an object contributes.
 * `axis` is 'x' (horizontal string) or 'y' (vertical string).
 */
const breaksForObject = (obj, scale, axis) => {
  const centre = axis === 'x' ? obj.x2D : obj.y2D;
  const size =
    axis === 'x' ? getObjectWidth(obj, scale) : getObjectHeight(obj, scale);
  if (!hasEdgeBreaks(obj)) return [{ position: centre, band: 'center', obj }];
  return [
    {
      position: centre - size / 2,
      band: axis === 'x' ? 'start' : 'start',
      obj,
    },
    { position: centre + size / 2, band: axis === 'x' ? 'end' : 'end', obj },
  ];
};

/**
 * Merge breaks within NODE_MERGE_M into one node, owned by the highest-priority
 * object there — the label and the extension-line target come from it.
 */
const toNodes = (breaks, scale) => {
  const tolerance = NODE_MERGE_M * scale;
  const sorted = [...breaks].sort((a, b) => a.position - b.position);
  const nodes = [];
  sorted.forEach((brk) => {
    const last = nodes[nodes.length - 1];
    if (last && Math.abs(last.position - brk.position) <= tolerance) {
      last.members.push(brk);
      const owner = last.members.reduce((best, m) =>
        (TYPE_PRIORITY[normalizedType(m.obj)] || 0) >
        (TYPE_PRIORITY[normalizedType(best.obj)] || 0)
          ? m
          : best
      );
      last.obj = owner.obj;
      last.band = owner.band;
      return;
    }
    nodes.push({
      position: brk.position,
      band: brk.band,
      obj: brk.obj,
      members: [brk],
    });
  });
  return nodes;
};

/** The outer edges of each contiguous run of objects (touching or overlapping). */
const runEdges = (objects, scale) => {
  const spans = objects
    .map((obj) => {
      const half = getObjectWidth(obj, scale) / 2;
      return { obj, start: obj.x2D - half, end: obj.x2D + half };
    })
    .sort((a, b) => a.start - b.start);

  const tolerance = NODE_MERGE_M * scale;
  const edges = [];
  let run = null;
  spans.forEach((span) => {
    if (run && span.start <= run.end + tolerance) {
      if (span.end > run.end) {
        run.end = span.end;
        run.endObj = span.obj;
      }
      return;
    }
    if (run) edges.push(run);
    run = {
      start: span.start,
      end: span.end,
      startObj: span.obj,
      endObj: span.obj,
    };
  });
  if (run) edges.push(run);

  return edges.flatMap((r) => [
    { position: r.start, band: 'start', obj: r.startObj },
    { position: r.end, band: 'end', obj: r.endObj },
  ]);
};

const pointFor = (node, axis, linePos) => {
  if (node.kind === 'wall') {
    return {
      type: node.type,
      label: node.label,
      x: axis === 'x' ? node.position : linePos,
      y: axis === 'x' ? linePos : node.position,
    };
  }
  return {
    type: `object-${node.band}`,
    label: `${getObjectTypeLabel(node.obj)} ${node.band}`,
    object: node.obj,
    // The extension line reaches the object it names, not the dimension line.
    x: axis === 'x' ? node.position : node.obj.x2D,
    y: axis === 'x' ? node.obj.y2D : node.position,
  };
};

/**
 * One string: consecutive nodes, wall end to wall end, all on one line.
 * Returns [] when there is nothing between the two ends — that is the overall
 * dimension, which its own tier already draws.
 */
const buildString = ({
  nodes,
  ends,
  axis,
  linePos,
  offset,
  scale,
  wallId,
  side,
  tier,
  subtype,
}) => {
  const points = [ends.start, ...nodes, ends.end];
  const stringKey = `str:${axis === 'x' ? 'h' : 'v'}:${side}:${tier}`;
  const measurements = [];

  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i];
    const to = points[i + 1];
    const distance = Math.abs(to.position - from.position) / scale;
    if (distance <= MIN_SEGMENT_M) continue;

    const fromPoint = pointFor(from, axis, linePos);
    const toPoint = pointFor(to, axis, linePos);
    measurements.push({
      type: axis === 'x' ? 'horizontal' : 'vertical',
      subtype,
      tier,
      stringKey,
      assignedSide: side,
      distance,
      fromLabel: fromPoint.label,
      toLabel: toPoint.label,
      offset,
      start:
        axis === 'x'
          ? { x: from.position, y: linePos }
          : { x: linePos, y: from.position },
      end:
        axis === 'x'
          ? { x: to.position, y: linePos }
          : { x: linePos, y: to.position },
      fromPoint,
      toPoint,
      wallId,
    });
  }

  return measurements;
};

const positionsMatch = (a, b, tolerance) =>
  a.every((pos) => b.some((other) => Math.abs(pos - other) <= tolerance));

/**
 * Build the tiered dimensions for one wall elevation.
 *
 * @param {Object} wallData        the wall (or wall group) being drawn.
 * @param {Array}  wallSegments    its segments, in draw order.
 * @param {Array}  projectedObjects objects projected onto the wall (px).
 * @param {number} scale           px per metre.
 * @returns {Array} measurements, in the renderer's shape.
 */
export const buildTieredElevationMeasurements = (
  wallData,
  wallSegments,
  projectedObjects,
  scale,
  geometry = TIER_GEOMETRY.screen
) => {
  const measurements = [];
  const segments = wallSegments || [];
  const tolerance = NODE_MERGE_M * scale;

  segments.forEach((segment, segmentIndex) => {
    const { width, height, x, wall, index } = segment;
    const wallId = wall?.id || `wall_${index}`;
    const pixelX = x * scale;
    const pixelWidth = width * scale;
    const pixelHeight = height * scale;
    const isFirstSegment = segmentIndex === 0;
    const isLastSegment = segmentIndex === segments.length - 1;

    const objects = (projectedObjects || []).filter(
      (obj) =>
        obj.x2D >= pixelX &&
        obj.x2D <= pixelX + pixelWidth &&
        measurableForTiers(obj)
    );

    const ends = {
      start: {
        kind: 'wall',
        position: pixelX,
        type: 'wall-start',
        label: 'Wall Edge',
      },
      end: {
        kind: 'wall',
        position: pixelX + pixelWidth,
        type: 'wall-end',
        label: 'Wall Edge',
      },
    };

    const low = objects.filter((obj) => !isHighObject(obj, scale, pixelHeight));
    const high = objects.filter((obj) => isHighObject(obj, scale, pixelHeight));

    // --- bottom: detail, major, overall ---
    const lowNodes = toNodes(
      low.flatMap((obj) => breaksForObject(obj, scale, 'x')),
      scale
    );
    const majorNodes = toNodes(
      [
        ...runEdges(
          low.filter((obj) => !isOpeningBreak(obj)),
          scale
        ),
        ...objects
          .filter(isOpeningBreak)
          .flatMap((obj) => breaksForObject(obj, scale, 'x')),
      ],
      scale
    );

    const lowPositions = lowNodes.map((n) => n.position);
    const majorPositions = majorNodes.map((n) => n.position);
    // A major string that says exactly what the detail string already says is
    // the same drawing twice.
    const majorAddsNothing =
      majorNodes.length === 0 ||
      (majorPositions.length === lowPositions.length &&
        positionsMatch(majorPositions, lowPositions, tolerance));

    const bottomTiers = [];
    if (lowNodes.length > 0)
      bottomTiers.push({ tier: 'detail', nodes: lowNodes });
    if (!majorAddsNothing)
      bottomTiers.push({ tier: 'major', nodes: majorNodes });
    bottomTiers.push({ tier: 'overall', nodes: [] });

    bottomTiers.forEach(({ tier, nodes }, tierIndex) => {
      measurements.push(
        ...buildString({
          nodes,
          ends,
          axis: 'x',
          linePos: pixelHeight,
          offset: geometry.baseOffsetPx + tierIndex * geometry.pitchPx,
          scale,
          wallId,
          side: 'bottom',
          tier,
          subtype: tier === 'overall' ? 'wall-total' : 'tier',
        })
      );
    });

    // --- top: the high objects, when they break the wall somewhere new ---
    const highNodes = toNodes(
      high.flatMap((obj) => breaksForObject(obj, scale, 'x')),
      scale
    );
    const highPositions = highNodes.map((n) => n.position);
    if (
      highNodes.length > 0 &&
      !positionsMatch(highPositions, lowPositions, tolerance)
    ) {
      measurements.push(
        ...buildString({
          nodes: highNodes,
          ends,
          axis: 'x',
          linePos: 0,
          offset: -geometry.baseOffsetPx,
          scale,
          wallId,
          side: 'top',
          tier: 'detail',
          subtype: 'tier',
        })
      );
    }

    // --- vertical: one side of the whole elevation ---
    if (!isFirstSegment && !isLastSegment) return;

    const groupStart = segments[0].x * scale;
    const groupEnd =
      (segments[segments.length - 1].x + segments[segments.length - 1].width) *
      scale;
    const verticalObjects = (projectedObjects || []).filter(
      (obj) =>
        obj.x2D >= groupStart &&
        obj.x2D <= groupEnd &&
        measurableForTiers(obj) &&
        !isHorizontalOnlyFixture(obj)
    );

    // The end nearer the objects' centre of mass: the extension lines are
    // shorter, and an empty wall keeps the left.
    const centroid =
      verticalObjects.length > 0
        ? verticalObjects.reduce((sum, obj) => sum + obj.x2D, 0) /
          verticalObjects.length
        : groupStart;
    const onLeft = centroid - groupStart <= groupEnd - centroid;
    if ((onLeft && !isFirstSegment) || (!onLeft && !isLastSegment)) return;

    const verticalNodes = toNodes(
      verticalObjects.flatMap((obj) => breaksForObject(obj, scale, 'y')),
      scale
    );
    const verticalEnds = {
      start: { kind: 'wall', position: 0, type: 'ceiling', label: 'Ceiling' },
      end: {
        kind: 'wall',
        position: pixelHeight,
        type: 'floor',
        label: 'Floor',
      },
    };
    const verticalLinePos = onLeft ? groupStart : groupEnd;
    const verticalSign = onLeft ? -1 : 1;
    const verticalTiers = verticalNodes.length
      ? ['heights', 'overall']
      : ['overall'];

    verticalTiers.forEach((tier, tierIndex) => {
      measurements.push(
        ...buildString({
          nodes: tier === 'overall' ? [] : verticalNodes,
          ends: verticalEnds,
          axis: 'y',
          linePos: verticalLinePos,
          offset:
            verticalSign *
            (geometry.baseOffsetPx + tierIndex * geometry.pitchPx),
          scale,
          wallId,
          side: onLeft ? 'left' : 'right',
          tier,
          subtype: tier === 'overall' ? 'wall-total' : 'tier',
        })
      );
    });
  });

  return measurements;
};
